import type {
  AiCallFilter,
  AiCallOrder,
  AnalyzeAiCallsResponse,
} from "@pax/api-contract/ai/calls/v1alpha1";
import { keepPreviousData, useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { api, reachable } from "@/lib/api";
import {
  type CallFilter,
  type Dimension,
  dimensions,
  faultNames,
  iconOf,
  NO_ERROR,
  nameOf,
  rememberNames,
} from "./dimensions";
import {
  addAll,
  type MetricInfo,
  mostOf,
  onlyKind,
  type Split,
  splitDimension,
  type Totals,
  tokenKindIds,
  tokenKinds,
} from "./measures";

/* ── Stretches of time ──────────────────────────────────────────────── */

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;

export type Range = "hour" | "day" | "week" | "month";

export const ranges: Record<
  Range,
  { label: string; buckets: number; bucketMs: number; before: string }
> = {
  hour: { label: "the last hour", buckets: 12, bucketMs: 5 * MINUTE, before: "the hour before" },
  day: { label: "the last 24 hours", buckets: 24, bucketMs: HOUR, before: "the 24 hours before" },
  week: { label: "the last 7 days", buckets: 28, bucketMs: 6 * HOUR, before: "the 7 days before" },
  month: {
    label: "the last 30 days",
    buckets: 30,
    bucketMs: 24 * HOUR,
    before: "the 30 days before",
  },
};

export const isRange = (value: unknown): value is Range =>
  typeof value === "string" && value in ranges;

/** A range ending at the close of the bucket now falls in, so every bucket is a whole one. */
export interface TimeWindow {
  range: Range;
  from: number;
  to: number;
  bucketMs: number;
  buckets: number;
}

export function windowOf(range: Range, now: number): TimeWindow {
  const { buckets, bucketMs } = ranges[range];
  const to = Math.ceil(now / bucketMs) * bucketMs;
  return { range, from: to - buckets * bucketMs, to, bucketMs, buckets };
}

const windowBefore = (window: TimeWindow): TimeWindow => ({
  ...window,
  from: window.from - (window.to - window.from),
  to: window.from,
});

const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export function clock(date: Date): string {
  const hours = date.getHours();
  const minutes = date.getMinutes();
  const suffix = hours < 12 ? "AM" : "PM";
  const hour = hours % 12 || 12;
  return minutes === 0
    ? `${hour} ${suffix}`
    : `${hour}:${String(minutes).padStart(2, "0")} ${suffix}`;
}

export const dayName = (date: Date) => `${months[date.getMonth()]} ${date.getDate()}`;

export const when = (date: Date) =>
  date.toDateString() === new Date().toDateString()
    ? clock(date)
    : `${dayName(date)}, ${clock(date)}`;

export function bucketName(from: number, to: number, range: Range): string {
  const start = new Date(from);
  const end = new Date(to);
  if (range === "month") return dayName(start);
  if (range === "week") return `${dayName(start)}, ${clock(start)} to ${clock(end)}`;
  return `${clock(start)} to ${clock(end)}`;
}

/* ── Asking the API ─────────────────────────────────────────────────── */

const apiFilter = ({ keep, promptOver }: CallFilter, window: TimeWindow): AiCallFilter => ({
  from: new Date(window.from).toISOString(),
  to: new Date(window.to).toISOString(),
  keep,
  ...(promptOver ? { promptTokensOver: promptOver } : {}),
});

/** A grouping with only a few values, for questions that only want the totals. */
const TOTALS_ONLY: Dimension = "outcome";

function useAnalysis(filter: CallFilter, groupBy: Dimension, window: TimeWindow) {
  return useQuery({
    queryKey: ["ai-calls-analyze", filter, groupBy, window.from, window.to, window.bucketMs],
    queryFn: async () => {
      const answer = await reachable(
        api.ai.calls.analyze({
          filter: apiFilter(filter, window),
          groupBy,
          bucketSeconds: window.bucketMs / 1000,
        }),
      );
      rememberNames(answer.names);
      return { ...answer, groupBy };
    },
    placeholderData: keepPreviousData,
    staleTime: 60_000,
  });
}

/** What every call a filter keeps adds up to. */
export function useTotals(filter: CallFilter, window: TimeWindow) {
  const analysis = useAnalysis(filter, TOTALS_ONLY, window);
  return { ...analysis, data: analysis.data?.totals };
}

/** The same question over the stretch of time just before, for how a measure changed. */
export const useTotalsBefore = (filter: CallFilter, window: TimeWindow) =>
  useTotals(filter, windowBefore(window));

/** The values a dimension takes among the calls a filter keeps, most calls first. */
export function useDimensionValues(dimension: Dimension, filter: CallFilter, window: TimeWindow) {
  const analysis = useAnalysis(filter, dimension, window);
  const answer = analysis.data?.groupBy === dimension ? analysis.data : undefined;
  return { ...analysis, data: answer?.groups.map(group => group.value) };
}

export const callSorts: Record<AiCallOrder, string> = {
  latest: "Latest",
  expensive: "Most expensive",
  slowest: "Slowest",
};

export const isCallSort = (value: unknown): value is AiCallOrder =>
  typeof value === "string" && value in callSorts;

/** Which outcomes a call list shows. */
export type CallShow = "all" | "failed" | "malformed";

export const callShows: Record<CallShow, string> = {
  all: "All",
  failed: "Failed",
  malformed: "Malformed",
};

export const isCallShow = (value: unknown): value is CallShow =>
  typeof value === "string" && value in callShows;

/** Matching calls a page at a time. */
export function useCallPages(
  filter: CallFilter,
  window: TimeWindow,
  sort: AiCallOrder,
  pageSize: number,
) {
  return useInfiniteQuery({
    queryKey: ["ai-calls-search", filter, sort, window.from, window.to, pageSize],
    queryFn: async ({ pageParam }) => {
      const page = await reachable(
        api.ai.calls.search({
          filter: apiFilter(filter, window),
          order: sort,
          limit: pageSize,
          ...(pageParam ? { cursor: pageParam } : {}),
        }),
      );
      rememberNames(page.names);
      return page;
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: page => page.nextCursor ?? undefined,
    placeholderData: keepPreviousData,
    staleTime: 60_000,
  });
}

/* ── The question as rows ───────────────────────────────────────────── */

export interface Row {
  key: string;
  label: string;
  icon?: string;
  about?: string;
  /** Pressing the row opens its profile, when it stands for a value of a dimension. */
  dimension?: Dimension;
  window: Totals;
  buckets: Totals[];
  /** The rows the Other row stands in for. */
  others?: Row[];
}

/** The rows the chart and table show by name. Past that, the rest add up to one Other row. */
const TOP = 8;
export const OTHER = "other";

function withOther(rows: Row[], dimension: Dimension | undefined): Row[] {
  if (rows.length <= TOP + 1) return rows;
  const others = rows.slice(TOP);
  return [
    ...rows.slice(0, TOP),
    {
      key: OTHER,
      label: "Other",
      about:
        `${others.length} more ${dimension ? dimensions[dimension].plural.toLowerCase() : ""}`.trim(),
      window: addAll(others.map(row => row.window)),
      buckets: (others[0]?.buckets ?? []).map((_, index) =>
        addAll(others.map(row => row.buckets[index] ?? row.window)),
      ),
      others,
    },
  ];
}

/** Each group's totals in each bucket, keyed by group, with empty buckets filled in. */
function bucketsByGroup(answer: AnalyzeAiCallsResponse, window: TimeWindow) {
  const byGroup = new Map<string, Totals[]>();
  for (const point of answer.series) {
    const index = Math.floor((Date.parse(point.start) - window.from) / window.bucketMs);
    if (index < 0 || index >= window.buckets) continue;
    let buckets = byGroup.get(point.value);
    if (!buckets) {
      buckets = Array.from({ length: window.buckets }, () => addAll([]));
      byGroup.set(point.value, buckets);
    }
    buckets[index] = point.totals;
  }
  return byGroup;
}

/** Every call's totals in each bucket, with empty buckets filled in. */
function overallBuckets(answer: AnalyzeAiCallsResponse, window: TimeWindow): Totals[] {
  const buckets = Array.from({ length: window.buckets }, () => addAll([]));
  for (const point of answer.totalSeries) {
    const index = Math.floor((Date.parse(point.start) - window.from) / window.bucketMs);
    if (index >= 0 && index < window.buckets) buckets[index] = point.totals;
  }
  return buckets;
}

function rowFor(dimension: Dimension, value: string, totals: Totals): Omit<Row, "buckets"> {
  if (dimension === "fault")
    return {
      key: value,
      label: faultNames[value] ?? value,
      about: value === NO_ERROR ? undefined : mostOf(totals.errorTypes),
      dimension,
      window: totals,
    };
  if (dimension === "errorType") {
    const fault = mostOf(totals.faults);
    return {
      key: value,
      label: nameOf(dimension, value),
      about: value === NO_ERROR || !fault ? undefined : faultNames[fault],
      dimension,
      window: totals,
    };
  }
  return {
    key: value,
    label: nameOf(dimension, value),
    icon: iconOf(dimension, value),
    dimension,
    window: totals,
  };
}

/**
 * One question: the calls a filter keeps over a window, split one way, as rows sorted by a
 * measure, with the totals in each bucket.
 */
export function useQuestion(
  filter: CallFilter,
  split: Split,
  window: TimeWindow,
  sortBy: MetricInfo,
  slowest: boolean,
) {
  const analysis = useAnalysis(filter, splitDimension(split) ?? TOTALS_ONLY, window);
  const answer = analysis.data;
  /** The grouping the answer on screen is for, which trails the split while a new one loads. */
  const dimension = answer && splitDimension(split) ? answer.groupBy : undefined;

  const shaped = useMemo(() => {
    if (!answer) return undefined;
    const byGroup = bucketsByGroup(answer, window);
    const empty = Array.from({ length: window.buckets }, () => addAll([]));
    const overall = overallBuckets(answer, window);

    let rows: Row[];
    if (split === "tokenKind")
      rows = tokenKindIds.map(kind => ({
        key: kind,
        label: tokenKinds[kind],
        window: onlyKind(answer.totals, kind),
        buckets: overall.map(totals => onlyKind(totals, kind)),
      }));
    else if (!dimension)
      rows = [{ key: "all", label: "All calls", window: answer.totals, buckets: overall }];
    else
      rows = answer.groups.map(group => ({
        ...rowFor(dimension, group.value, group.totals),
        buckets: byGroup.get(group.value) ?? empty,
      }));

    const sortValue = (row: Row) => sortBy.value(row.window, slowest) ?? Number.NEGATIVE_INFINITY;
    rows.sort((left, right) => sortValue(right) - sortValue(left));
    return { rows: withOther(rows, dimension), window: answer.totals, buckets: overall };
  }, [answer, window, split, dimension, sortBy, slowest]);

  return { ...analysis, shaped };
}
